import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Button,
  Stack,
  Group,
  Alert,
  Text,
  Title,
  Badge
} from '@mantine/core';
import { IconAlertCircle, IconCrown, IconSparkles } from '@tabler/icons-react';
import { api } from '../../services/api';

const SpecialRequestForm = ({ opened, onClose, princessId, princessName, onSuccess }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [formData, setFormData] = useState({
    nail_polish_color: '',
    design: '',
    service_type: 'gel',
    special_notes: '',
    number_of_pictures: 5,
    offer_price: 50.00
  });

  useEffect(() => {
    // Check subscription status when modal opens
    if (opened) {
      console.log('SpecialRequestForm - User data:', user);
      console.log('SpecialRequestForm - Subscription:', user?.subscription);
      
      if (!isAuthenticated) {
        console.log('SpecialRequestForm - Not authenticated, showing upgrade prompt');
        setShowUpgradePrompt(true);
      } else if (!user?.subscription || user.subscription.plan !== 'paypiggiepro' || user.subscription.status !== 'active') {
        console.log('SpecialRequestForm - No PayPiggyPro subscription, showing upgrade prompt');
        console.log('  Has subscription:', !!user?.subscription);
        console.log('  Plan:', user?.subscription?.plan);
        console.log('  Status:', user?.subscription?.status);
        setShowUpgradePrompt(true);
      } else {
        console.log('SpecialRequestForm - Has PayPiggyPro, showing form');
        setShowUpgradePrompt(false);
      }
    }
  }, [opened, isAuthenticated, user]);

  const serviceTypes = [
    { value: 'paraffin', label: 'Paraffin' },
    { value: 'french', label: 'French' },
    { value: 'gel', label: 'Gel' },
    { value: 'athletic', label: 'Athletic' }
  ];

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.createSpecialRequest({
        ...formData,
        princess_id: princessId
      });

      onSuccess(response);
      handleClose();
    } catch (err) {
      console.error('Error creating special request:', err);
      setError(err.message || 'Failed to create special request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      nail_polish_color: '',
      design: '',
      service_type: 'gel',
      special_notes: '',
      number_of_pictures: 5,
      offer_price: 50.00
    });
    setError(null);
    onClose();
  };

  const handleUpgradeClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate('/subscription');
    }
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Special Request for ${princessName}`}
      size="lg"
    >
      <Stack>
        {showUpgradePrompt ? (
          <Stack align="center" spacing="lg" py="xl">
            <IconCrown size={64} style={{ color: 'var(--mantine-color-yellow-6)' }} />
            <Title order={3} ta="center">PayPiggyPro Exclusive Feature</Title>
            <Text ta="center" size="lg" c="dimmed">
              Special Requests are only available to PayPiggyPro subscribers
            </Text>
            <Badge size="xl" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
              $25/month
            </Badge>
            <Text ta="center" size="sm" c="dimmed" maw={400}>
              Upgrade to PayPiggyPro to unlock custom nail service requests, priority messaging, and exclusive content from your favorite princesses!
            </Text>
            <Group>
              <Button 
                size="lg"
                variant="gradient"
                gradient={{ from: 'blue', to: 'cyan' }}
                leftSection={<IconSparkles size="1.2rem" />}
                onClick={handleUpgradeClick}
              >
                {!isAuthenticated ? 'Login to Subscribe' : 'Upgrade Now'}
              </Button>
              <Button variant="subtle" onClick={handleClose}>
                Maybe Later
              </Button>
            </Group>
          </Stack>
        ) : (
          <>
            {error && (
              <Alert icon={<IconAlertCircle size="1rem" />} color="red">
                {error}
              </Alert>
            )}

            <Text size="sm" c="dimmed">
              Create a custom nail service request with your specific preferences.
            </Text>

            <TextInput
              label="Nail Polish Color"
              placeholder="e.g., Ruby Red, Ocean Blue"
              value={formData.nail_polish_color}
              onChange={(e) => setFormData({ ...formData, nail_polish_color: e.target.value })}
            />

            <Textarea
              label="Design Description"
              placeholder="Describe the design you'd like..."
              minRows={3}
              value={formData.design}
              onChange={(e) => setFormData({ ...formData, design: e.target.value })}
            />

            <Select
              label="Service Type"
              data={serviceTypes}
              value={formData.service_type}
              onChange={(value) => setFormData({ ...formData, service_type: value })}
            />

            <Textarea
              label="Special Notes"
              placeholder="Any additional requests or preferences..."
              minRows={2}
              value={formData.special_notes}
              onChange={(e) => setFormData({ ...formData, special_notes: e.target.value })}
            />

            <NumberInput
              label="Number of Pictures"
              description="How many photos would you like?"
              min={1}
              max={20}
              value={formData.number_of_pictures}
              onChange={(value) => setFormData({ ...formData, number_of_pictures: value })}
            />

            <NumberInput
              label="Offer Price ($)"
              description="Your offer for this custom service"
              min={10}
              max={500}
              precision={2}
              value={formData.offer_price}
              onChange={(value) => setFormData({ ...formData, offer_price: value })}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} loading={loading}>
                Submit Request
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default SpecialRequestForm;